import { createContext, useContext } from "react";
import { PageType } from "@/constants/PageType";
import usePresets from "@/hooks/presets/usePresets";
import { useRememberedSelection } from "@/hooks/userState/useRememberedSelection";
import { Preset } from "@/models/Preset";

interface PresetContextProps {
  presets: Preset[];
  selectedPreset: Preset | null;
  setSelectedPreset: (next: Preset | null) => void;
  /** Presets and the remembered selection have loaded: a null `selectedPreset` is final. */
  isReady: boolean;
}

const PresetContext = createContext<PresetContextProps | undefined>(undefined);

export const PresetProvider: React.FC<{
  children: React.ReactNode;
  initialPresets?: Preset[];
  /**
   * Names whose preset selection this is, e.g. `selectedPreset:faultMonitor`, so pages do not
   * overwrite each other's choice.
   */
  selectionKey: string;
}> = ({ children, initialPresets, selectionKey }) => {
  // useAddinData shows an empty placeholder while loading, so `isLoading` is never true here.
  // A failed query also counts as ready: the grid then falls back to its defaults.
  const { data: fetchedPresets = [], isPlaceholderData: presetsLoading } = usePresets();
  const presets = initialPresets ?? fetchedPresets;
  const {
    selected: selectedPreset,
    setSelected: setSelectedPreset,
    isReady: selectionReady,
  } = useRememberedSelection(selectionKey, presets);

  return (
    <PresetContext.Provider
      value={{
        presets,
        selectedPreset,
        setSelectedPreset,
        isReady: selectionReady && (!!initialPresets || !presetsLoading),
      }}
    >
      {children}
    </PresetContext.Provider>
  );
};

export const usePresetContext = (pageType?: PageType): PresetContextProps => {
  const context = useContext(PresetContext);
  if (pageType === "maintenance") {
    return {
      presets: [],
      selectedPreset: { id: "maintenance", name: "Maintenance", columns: [] } as Preset,
      setSelectedPreset: () => {},
      isReady: true,
    };
  }

  if (!context) {
    throw new Error("usePresetContext must be used within a PresetProvider");
  }
  return context;
};
